import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import { format } from 'date-fns';
import { 
  Plus, Search, Filter, Truck, CheckCircle, Clock,
  ChevronRight, RefreshCw, Download, X, Star, AlertTriangle,
  TrendingUp, TrendingDown
} from 'lucide-react';

const VendorManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [filters, setFilters] = useState({ status: '', category: '', search: '' });
  const [activeTab, setActiveTab] = useState('list');

  const fetchVendors = useCallback(async () => {
    try {
      let url = '/vendors';
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await apiClient.get(url);
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/vendors/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchStats();
  }, [fetchVendors]);

  const getRatingColor = (rating) => {
    switch (rating) {
      case 'Excellent': return 'text-green-600 bg-green-100';
      case 'Good': return 'text-blue-600 bg-blue-100';
      case 'Acceptable': return 'text-yellow-600 bg-yellow-100';
      case 'Poor': return 'text-orange-600 bg-orange-100';
      case 'Unacceptable': return 'text-blue-600 bg-red-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendor-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Vendor Management</h1>
          <p className="text-slate-500">Supplier Performance & Scoring</p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Vendor
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Vendors</p>
              <p className="text-2xl font-bold text-slate-800">{stats?.total_vendors || 0}</p>
            </div>
            <Truck className="h-8 w-8 text-slate-300" />
          </div>
        </div>
        <div className="bg-white border border-green-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Active</p>
              <p className="text-2xl font-bold text-green-600">{stats?.active_vendors || 0}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-300" />
          </div>
        </div>
        <div className="bg-white border border-yellow-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">On Probation</p>
              <p className="text-2xl font-bold text-yellow-600">{stats?.probation_vendors || 0}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-yellow-300" />
          </div>
        </div>
        <div className="bg-white border border-blue-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Blocked</p>
              <p className="text-2xl font-bold text-blue-600">{stats?.blocked_vendors || 0}</p>
            </div>
            <X className="h-8 w-8 text-blue-300" />
          </div>
        </div>
        <div className="bg-white border border-blue-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Avg Score</p>
              <p className="text-2xl font-bold text-blue-600">{stats?.average_score || 0}%</p>
            </div>
            <Star className="h-8 w-8 text-blue-300" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <nav className="flex gap-6 px-4">
          {[
            { id: 'list', label: 'Vendor List' },
            { id: 'action', label: 'Needs Action' },
            { id: 'top', label: 'Top Performers' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
              {tab.id === 'action' && stats?.vendors_needing_action?.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-100 text-blue-600 rounded-full text-xs">
                  {stats.vendors_needing_action.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'list' && (
        <>
          {/* Filters */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-4 flex-wrap">
              <Filter className="h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search vendors..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm w-64"
              />
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="probation">Probation</option>
                <option value="inactive">Inactive</option>
                <option value="blocked">Blocked</option>
              </select>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                <option value="">All Categories</option>
                <option value="raw_materials">Raw Materials</option>
                <option value="components">Components</option>
                <option value="services">Services</option>
                <option value="equipment">Equipment</option>
              </select>
            </div>
          </div>

          {/* Vendor List */}
          <div className="space-y-4">
            {vendors.map((vendor) => (
              <div
                key={vendor.id}
                onClick={() => setSelectedVendor(vendor)}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:border-blue-300 cursor-pointer transition-all shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-mono text-slate-500">{vendor.vendor_code}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        vendor.status === 'active' ? 'bg-green-100 text-green-800' :
                        vendor.status === 'probation' ? 'bg-yellow-100 text-yellow-800' :
                        vendor.status === 'blocked' ? 'bg-red-100 text-blue-700' :
                        'bg-slate-100 text-slate-800'
                      }`}>
                        {vendor.status}
                      </span>
                      {vendor.total_ncrs > 0 && (
                        <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs">
                          {vendor.total_ncrs} NCRs
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-slate-800 text-lg">{vendor.company_name}</h3>
                    <p className="text-slate-500 text-sm">{vendor.contact_name} • {vendor.email}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-800">{vendor.current_score || 0}%</p>
                      <p className={`text-sm font-medium px-2 py-0.5 rounded ${getRatingColor(vendor.score_rating)}`}>
                        {vendor.score_rating || 'Not Rated'}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </div>
                </div>
              </div>
            ))}
            {vendors.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Truck className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>No vendors found</p>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'action' && (
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="text-yellow-800 font-medium">Vendors scoring below 70% require attention</p>
          </div>
          {stats?.vendors_needing_action?.map((vendor) => (
            <div
              key={vendor.id}
              className="bg-white border border-blue-200 rounded-xl p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800">{vendor.company_name}</h3>
                  <p className="text-slate-500 text-sm">{vendor.vendor_code}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-600">{vendor.current_score}%</p>
                  <p className={`text-sm font-medium ${getRatingColor(vendor.score_rating)}`}>
                    {vendor.score_rating}
                  </p>
                </div>
              </div>
            </div>
          ))}
          {(!stats?.vendors_needing_action || stats.vendors_needing_action.length === 0) && (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-300" />
              <p>All vendors are performing well!</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'top' && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-green-800 font-medium">Top performing vendors (90%+ score)</p>
          </div>
          {stats?.top_performers?.map((vendor, idx) => (
            <div
              key={vendor.id}
              className="bg-white border border-green-200 rounded-xl p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-700 font-bold">#{idx + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{vendor.company_name}</h3>
                    <p className="text-slate-500 text-sm">{vendor.vendor_code}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">{vendor.current_score}%</p>
                  <p className="text-sm font-medium text-green-700">Excellent</p>
                </div>
              </div>
            </div>
          ))}
          {(!stats?.top_performers || stats.top_performers.length === 0) && (
            <div className="text-center py-12 text-slate-500">
              <Star className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No top performers yet. Evaluate your vendors!</p>
            </div>
          )}
        </div>
      )}

      {/* Vendor Detail Modal */}
      {selectedVendor && (
        <VendorDetailModal
          vendor={selectedVendor}
          onClose={() => setSelectedVendor(null)}
          onScore={() => {
            setShowScoreModal(true);
          }}
          onRefresh={fetchVendors}
        />
      )}

      {/* New Vendor Modal */}
      {showNewModal && (
        <NewVendorModal
          onClose={() => setShowNewModal(false)}
          onCreated={() => {
            fetchVendors();
            fetchStats();
            setShowNewModal(false);
          }}
        />
      )}

      {/* Score Modal */}
      {showScoreModal && selectedVendor && (
        <ScoreVendorModal
          vendor={selectedVendor}
          onClose={() => setShowScoreModal(false)}
          onScored={() => {
            fetchVendors();
            fetchStats();
            setShowScoreModal(false);
            setSelectedVendor(null);
          }}
        />
      )}
    </div>
  );
};

// Vendor Detail Modal
const VendorDetailModal = ({ vendor, onClose, onScore, onRefresh }) => {
  const [scores, setScores] = useState([]);
  const [ncrs, setNcrs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [scoresRes, ncrsRes] = await Promise.all([
          apiClient.get(`/vendors/${vendor.id}/scores`),
          apiClient.get(`/ncrs?vendor_id=${vendor.id}`)
        ]);
        setScores(scoresRes.data);
        setNcrs(ncrsRes.data);
      } catch (error) {
        console.error('Error fetching vendor data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [vendor.id]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">{vendor.vendor_code}</p>
              <h2 className="text-2xl font-bold text-slate-800">{vendor.company_name}</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Score Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 p-4 rounded-lg text-center">
              <p className="text-4xl font-bold text-slate-800">{vendor.current_score || 0}%</p>
              <p className={`text-sm font-medium mt-1 px-2 py-0.5 rounded inline-block ${
                vendor.score_rating === 'Excellent' ? 'bg-green-100 text-green-700' :
                vendor.score_rating === 'Good' ? 'bg-blue-100 text-blue-700' :
                vendor.score_rating === 'Acceptable' ? 'bg-yellow-100 text-yellow-700' :
                vendor.score_rating === 'Poor' ? 'bg-orange-100 text-orange-700' :
                'bg-red-100 text-blue-600'
              }`}>
                {vendor.score_rating || 'Not Rated'}
              </p>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-slate-500">Status</p>
                  <p className="font-medium capitalize">{vendor.status}</p>
                </div>
                <div>
                  <p className="text-slate-500">Total NCRs</p>
                  <p className="font-medium">{vendor.total_ncrs || 0}</p>
                </div>
                <div>
                  <p className="text-slate-500">Category</p>
                  <p className="font-medium capitalize">{vendor.category?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-slate-500">Last Eval</p>
                  <p className="font-medium">
                    {vendor.last_evaluation_date 
                      ? format(new Date(vendor.last_evaluation_date), 'MMM dd, yyyy')
                      : 'Never'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-slate-50 p-4 rounded-lg">
            <h3 className="font-semibold text-slate-800 mb-2">Contact Information</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <p><span className="text-slate-500">Contact:</span> {vendor.contact_name || 'N/A'}</p>
              <p><span className="text-slate-500">Email:</span> {vendor.email || 'N/A'}</p>
              <p><span className="text-slate-500">Phone:</span> {vendor.phone || 'N/A'}</p>
              <p><span className="text-slate-500">Address:</span> {vendor.address || 'N/A'}</p>
            </div>
          </div>

          {/* Score History */}
          <div>
            <h3 className="font-semibold text-slate-800 mb-3">Score History</h3>
            {scores.length > 0 ? (
              <div className="space-y-2">
                {scores.slice(0, 5).map((score) => (
                  <div key={score.id} className="bg-slate-50 p-3 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium">{score.evaluation_period}</p>
                      <p className="text-xs text-slate-500">
                        Q: {score.quality_score}% | D: {score.delivery_score}% | C: {score.cost_score}% | S: {score.service_score}%
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{score.total_score}%</p>
                      <p className="text-xs text-slate-500">{score.rating}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No evaluations yet</p>
            )}
          </div>

          {/* NCRs */}
          {ncrs.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-800 mb-3">Recent NCRs</h3>
              <div className="space-y-2">
                {ncrs.slice(0, 3).map((ncr) => (
                  <div key={ncr.id} className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-blue-700">{ncr.ncr_number}</p>
                        <p className="text-xs text-blue-600 line-clamp-1">{ncr.description}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        ncr.status === 'closed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-blue-600'
                      }`}>
                        {ncr.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              onClick={onScore}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add Evaluation
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// New Vendor Modal
const NewVendorModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    vendor_code: '',
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    category: 'general',
    status: 'active',
    certifications: [],
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post('/vendors', formData);
      onCreated();
    } catch (error) {
      console.error('Error creating vendor:', error);
      alert(error.response?.data?.detail || 'Error creating vendor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-800">Add New Vendor</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Vendor Code</label>
              <input
                type="text"
                required
                value={formData.vendor_code}
                onChange={(e) => setFormData({ ...formData, vendor_code: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                placeholder="V-001"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                <option value="general">General</option>
                <option value="raw_materials">Raw Materials</option>
                <option value="components">Components</option>
                <option value="services">Services</option>
                <option value="equipment">Equipment</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
            <input
              type="text"
              required
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name</label>
              <input
                type="text"
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Add Vendor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Score Vendor Modal
const ScoreVendorModal = ({ vendor, onClose, onScored }) => {
  const [formData, setFormData] = useState({
    vendor_id: vendor.id,
    evaluation_period: `Q${Math.ceil((new Date().getMonth() + 1) / 3)} ${new Date().getFullYear()}`,
    quality_score: 80,
    delivery_score: 80,
    cost_score: 80,
    service_score: 80,
    evaluator_id: 'current-user',
    evaluator_name: 'Current User',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const calculatedTotal = (
    formData.quality_score * 0.4 +
    formData.delivery_score * 0.3 +
    formData.cost_score * 0.15 +
    formData.service_score * 0.15
  ).toFixed(1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post(`/vendors/${vendor.id}/scores`, formData);
      onScored();
    } catch (error) {
      console.error('Error scoring vendor:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Evaluate Vendor</h2>
            <p className="text-slate-500 text-sm">{vendor.company_name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Evaluation Period</label>
            <input
              type="text"
              required
              value={formData.evaluation_period}
              onChange={(e) => setFormData({ ...formData, evaluation_period: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg"
            />
          </div>

          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700">Quality (40%)</label>
                <span className="text-sm font-bold">{formData.quality_score}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={formData.quality_score}
                onChange={(e) => setFormData({ ...formData, quality_score: parseInt(e.target.value) })}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700">Delivery (30%)</label>
                <span className="text-sm font-bold">{formData.delivery_score}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={formData.delivery_score}
                onChange={(e) => setFormData({ ...formData, delivery_score: parseInt(e.target.value) })}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700">Cost (15%)</label>
                <span className="text-sm font-bold">{formData.cost_score}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={formData.cost_score}
                onChange={(e) => setFormData({ ...formData, cost_score: parseInt(e.target.value) })}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-slate-700">Service (15%)</label>
                <span className="text-sm font-bold">{formData.service_score}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={formData.service_score}
                onChange={(e) => setFormData({ ...formData, service_score: parseInt(e.target.value) })}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <p className="text-sm text-slate-500">Calculated Total Score</p>
            <p className="text-3xl font-bold text-slate-800">{calculatedTotal}%</p>
            <p className={`text-sm font-medium ${
              calculatedTotal >= 90 ? 'text-green-600' :
              calculatedTotal >= 80 ? 'text-blue-600' :
              calculatedTotal >= 70 ? 'text-yellow-600' :
              calculatedTotal >= 60 ? 'text-orange-600' :
              'text-blue-600'
            }`}>
              {calculatedTotal >= 90 ? 'Excellent' :
               calculatedTotal >= 80 ? 'Good' :
               calculatedTotal >= 70 ? 'Acceptable' :
               calculatedTotal >= 60 ? 'Poor' :
               'Unacceptable'}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Submit Evaluation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VendorManagement;
